import { useState, useEffect } from 'react';

import { ArrowUpDown } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { LabellerFactViewer } from './fact-viewer';
import { useIncludedFacts } from './useIncludedFacts';
import {
  InconsistencyLevel,
  InconsistencySource,
  InconsistencyType,
  getInconsistencySourceDisplayName,
  getInconsistencyTypeDisplayName,
} from '@/../../backend/src/matter/inconsistency/inconsistency-display';
import { useCreateInconsistencyFromFacts } from '@/api/mutations/useCreateInconsistencyFromFacts';
import { Button } from '@/new/components/shadcn/button';
import { Dialog, DialogContent } from '@/new/components/shadcn/dialog';
import { Label } from '@/new/components/shadcn/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/new/components/shadcn/select';
import { Textarea } from '@/new/components/shadcn/textarea';

interface CreateInconsistencyModalProps {
  isOpen: boolean;
  onClose: () => void;
  caseId: string;
  onSuccess: () => void;
}

interface FactAssignment {
  factId: string;
  groupName: string;
}

const inconsistencyTypes = Object.values(InconsistencyType).map((type) => ({
  value: type,
  label: getInconsistencyTypeDisplayName(type),
}));

const inconsistencySources = Object.values(InconsistencySource).map((source) => ({
  value: source,
  label: getInconsistencySourceDisplayName(source),
}));

const inconsistencyLevels = [
  { value: InconsistencyLevel.Minor, label: 'Minor' },
  { value: InconsistencyLevel.Severe, label: 'Severe' },
  { value: InconsistencyLevel.Critical, label: 'Critical' },
];

// Get group options based on inconsistency source (always 2 groups)
const getGroupOptions = (source: InconsistencySource) => {
  switch (source) {
    case InconsistencySource.DepositionVsEvidence:
      return ['Claim', 'Evidence'];
    case InconsistencySource.MultipleDepositions:
      return ['Claim', 'Different Claim'];
    case InconsistencySource.SameDeposition:
      return ['Claim', 'Different Claim'];
    case InconsistencySource.EvidenceVsEvidence:
      return ['Evidence', 'Different Evidence'];
  }
};

export const CreateInconsistencyModal = ({ isOpen, onClose, caseId, onSuccess }: CreateInconsistencyModalProps) => {
  const { matterId, subsetId } = useParams();
  const { facts, currentFactIndex, currentFact, handlePrevFact, handleNextFact } = useIncludedFacts(
    matterId ?? '',
    caseId,
    isOpen,
  );

  const [factAssignments, setFactAssignments] = useState<FactAssignment[]>([]);
  const [description, setDescription] = useState('');
  const [type, setType] = useState<InconsistencyType>(InconsistencyType.Events);
  const [source, setSource] = useState<InconsistencySource>(InconsistencySource.DepositionVsEvidence);
  const [level, setLevel] = useState<InconsistencyLevel>(InconsistencyLevel.Severe);

  useEffect(() => {
    if (isOpen && facts.length > 0) {
      setDescription('');
      setType(InconsistencyType.Events);
      setSource(InconsistencySource.DepositionVsEvidence);
      setLevel(InconsistencyLevel.Severe);

      const groupOptions = getGroupOptions(InconsistencySource.DepositionVsEvidence);
      const initialAssignments = facts.map((fact) => ({
        factId: fact.id,
        groupName: groupOptions[0],
      }));
      setFactAssignments(initialAssignments);
    }
  }, [isOpen, facts.length]);

  // Reset assignments when source changes
  useEffect(() => {
    if (facts.length > 0 && factAssignments.length > 0) {
      const groupOptions = getGroupOptions(source);
      const updatedAssignments = factAssignments.map((assignment) => ({
        ...assignment,
        groupName: groupOptions[0],
      }));
      setFactAssignments(updatedAssignments);
    }
  }, [source]);

  const { mutate: createInconsistency, isLoading } = useCreateInconsistencyFromFacts(matterId ?? '', subsetId ?? '', {
    onSuccess: () => {
      toast.success('Inconsistency created successfully');
      onSuccess();
      onClose();
    },
  });

  const handleCreate = () => {
    if (!description.trim() || factAssignments.length === 0) {
      return;
    }

    // Ensure both groups have at least 1 fact
    if (group1Facts.length === 0 || group2Facts.length === 0) {
      return;
    }

    createInconsistency({
      factAssignments,
      type,
      source,
      level,
      description,
    });
  };

  const updateFactAssignment = (factId: string, groupName: string) => {
    setFactAssignments((prev) =>
      prev.map((assignment) => (assignment.factId === factId ? { ...assignment, groupName } : assignment)),
    );
  };

  // Get dynamic group options based on current source
  const groupOptions = getGroupOptions(source);

  // Get the group name for the current fact
  const currentFactAssignment = factAssignments.find((a) => a.factId === currentFact?.id);

  // Group facts by their assignments for preview
  const group1Facts = facts.filter((f) => {
    const assignment = factAssignments.find((a) => a.factId === f.id);
    return assignment?.groupName === groupOptions[0];
  });
  const group2Facts = facts.filter((f) => {
    const assignment = factAssignments.find((a) => a.factId === f.id);
    return assignment?.groupName === groupOptions[1];
  });

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[calc(100vw-4rem)]">
        <div className="flex max-h-[70vh]">
          {/* Left Panel - Selected Facts with Group Assignment */}
          <LabellerFactViewer
            facts={facts}
            currentFactIndex={currentFactIndex}
            onPrevious={handlePrevFact}
            onNext={handleNextFact}
          >
            {currentFact && (
              <div>
                <Label>Assign to Group</Label>
                <Select
                  value={currentFactAssignment?.groupName || groupOptions[0]}
                  onValueChange={(value) => updateFactAssignment(currentFact.id, value)}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {groupOptions.map((option) => (
                      <SelectItem key={option} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </LabellerFactViewer>

          {/* Right Panel - Inconsistency Configuration */}
          <div className="w-1/2 overflow-y-auto p-6">
            <h3 className="mb-4 text-lg font-semibold">Create Inconsistency</h3>

            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="type">Type</Label>
                  <Select value={type} onValueChange={(value) => setType(value as InconsistencyType)}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {inconsistencyTypes.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="source">Source</Label>
                  <Select value={source} onValueChange={(value) => setSource(value as InconsistencySource)}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {inconsistencySources.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="level">Severity</Label>
                  <Select value={level} onValueChange={(value) => setLevel(value as InconsistencyLevel)}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {inconsistencyLevels.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe the inconsistency"
                  className="mt-1"
                  rows={3}
                />
              </div>

              <div className="mt-6 border-t pt-4">
                {factAssignments.length > 0 ? (
                  <div className="space-y-3">
                    {/* First Group */}
                    <div className="rounded-lg bg-blue-50/50 p-3">
                      <div className="mb-2 text-sm font-medium text-blue-900">
                        {groupOptions[0]} ({group1Facts.length} facts)
                      </div>
                      <div className="space-y-1">
                        {group1Facts.slice(0, 3).map((fact) => (
                          <div key={fact.id} className="text-xs text-gray-700">
                            • {fact.description?.substring(0, 100)}...
                          </div>
                        ))}
                        {group1Facts.length > 3 && (
                          <div className="text-xs text-gray-500">...and {group1Facts.length - 3} more</div>
                        )}
                      </div>
                    </div>

                    {/* Arrow Indicator */}
                    <div className="flex justify-center">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full border bg-gray-100">
                        <ArrowUpDown className="h-4 w-4 text-gray-600" />
                      </div>
                    </div>

                    {/* Second Group */}
                    <div className="rounded-lg bg-red-50/50 p-3">
                      <div className="mb-2 text-sm font-medium text-red-900">
                        {groupOptions[1]} ({group2Facts.length} facts)
                      </div>
                      <div className="space-y-1">
                        {group2Facts.slice(0, 3).map((fact) => (
                          <div key={fact.id} className="text-xs text-gray-700">
                            • {fact.description?.substring(0, 100)}...
                          </div>
                        ))}
                        {group2Facts.length > 3 && (
                          <div className="text-xs text-gray-500">...and {group2Facts.length - 3} more</div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-gray-500">Loading preview...</div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4">
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleCreate}
            disabled={!description.trim() || isLoading || group1Facts.length === 0 || group2Facts.length === 0}
          >
            {isLoading ? 'Creating...' : 'Create Inconsistency'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
